import React, { useState } from 'react';
import { SummaryCard } from '../../../myAudit/components/SummaryCard';
import { useLocation, useNavigate } from 'react-router-dom';
import { getItem, setItem } from '../../../../utils/localStorage';
import { createMyAudit } from '../../../../requests/myAudit';
import { ButtonLarge } from '../../../common/sharedComponents/buttons/ButtonLarge';
import { useTranslation } from 'react-i18next';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../helpers/showMessage';
import { ButtonBlockColumn } from './../../../common/sharedStyledComponents/GenericStyles.styled';
import { Headline3 } from '../../../common/typography';
import {
  ButtonContainer,
  HeaderContainer,
  StyledMyAuditContainer,
} from '../../../common/sharedStyledComponents/MyAuditGenericStyles.styled';
import { AppHeader } from '../../../common/sharedComponents/AppHeader';
import { sentryReport } from '../../../../helpers/sentry';
import { PreviewSkeleton } from './../../MyAuditSkeleton/PreviewSkeleton';
import { Modal } from '../../../common/sharedComponents/Modal';
import { DeleteTitleContainer } from '../MyAuditList/styled.myAuditList';
import {
  IMyAuditPayload,
  IMyAuditPayloadSection,
  IMyAuditPayloadSectionCreation,
  IMyAuditPreviewCard,
} from '../../../@types/myAudit';
import { Nullable } from '../../../@types/genericTypes';

export function MyAuditPreview () {
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const { myAuditPreview, templateName } = state;
  const myAudit: IMyAuditPayload = getItem('myAudit');
  const { t } = useTranslation();
  const [myAuditCreation, setMyAuditCreation] =
    useState<IMyAuditPayload>(myAudit);
  const [myAuditPreviewCards, setMyAuditPreviewCards] =
    useState<IMyAuditPreviewCard[]>(myAuditPreview);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [auditorIdToDelete, setAuditorIdToDelete] =
    useState<Nullable<number>>();
  const [isSending, setIsSending] = useState<boolean>(false);

  const handleSubmit = () => {
    setIsSending(true);
    createMyAudit(myAuditCreation)
      .then(() => {
        showSuccessMessage(t('COMMON.MY_AUDIT.ASSIGN_FORM.SUCCESS'));
        setItem('isFormData', false);
        navigate('/myactions?filterBy=received');
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          summaryError: t('COMMON.MY_AUDIT.ASSIGN_FORM.ERROR'),
          path: '/myaudit',
        });
        showErrorMessage(t('COMMON.MY_AUDIT.ASSIGN_FORM.ERROR'));
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const handleEdit = (auditorId: Nullable<number>) => {
    const myAuditCopy: IMyAuditPayload = { ...myAuditCreation };
    const myAuditPreviewCarsCopy: IMyAuditPreviewCard[] = [
      ...myAuditPreviewCards,
    ];
    const filteredSection = myAuditCopy.sections.filter(
      (section) => section.auditorId !== auditorId
    );
    const filteredMyAuditPreviewCards: IMyAuditPreviewCard[] =
      myAuditPreviewCarsCopy.filter(
        (previewCard) => auditorId !== previewCard.auditor!.id
      );

    myAuditCopy.sections = filteredSection;
    myAuditCopy.subject = filteredSection.length > 0 ? myAuditCopy.subject : '';

    setItem('myAudit', myAuditCopy);

    navigate('/myaudit/sections', {
      state: {
        templateName,
        myAuditPreview: filteredMyAuditPreviewCards,
        currentAuditorIndex: filteredMyAuditPreviewCards.length,
      },
    });
  };

  const handleDelete = (auditorId: Nullable<number>) => {
    const myAuditCopy: IMyAuditPayload = { ...myAuditCreation };
    const myAuditPreviewCarsCopy: IMyAuditPreviewCard[] = [
      ...myAuditPreviewCards,
    ];
    const filteredMyAuditSections: IMyAuditPayloadSectionCreation[] =
      myAuditCopy.sections.filter(
        (section: IMyAuditPayloadSection) => section.auditorId !== auditorId
      );
    const filteredMyAuditPreviewCards: IMyAuditPreviewCard[] =
      myAuditPreviewCarsCopy.filter(
        (previewCard) => auditorId !== previewCard.auditor!.id
      );
    myAuditCopy.sections = filteredMyAuditSections;

    setMyAuditCreation(myAuditCopy);
    setMyAuditPreviewCards(filteredMyAuditPreviewCards);
    if (myAuditCopy.sections.length === 0) {
      navigate('/myaudit/checklist');
    }
    setShowDeleteModal(false);
  };

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  return (
    <>
      <HeaderContainer>
        <AppHeader title={t('COMMON.MY_AUDIT.TITLE')} hasBackButton={false} />
      </HeaderContainer>
      <StyledMyAuditContainer>
        <Headline3>{t('COMMON.MY_AUDIT.PREVIEW.HEADING')}</Headline3>
        {myAuditCreation.sections.length <= 0 ? (
          <PreviewSkeleton />
        ) : (
          <>
            {myAuditPreviewCards.length > 0 &&
              myAuditPreviewCards.map((auditorCard) => {
                return (
                  <SummaryCard
                    key={auditorCard.auditor!.id}
                    percentage={0}
                    department={templateName}
                    sections={auditorCard.sectionsCard!.names!}
                    dueDate={auditorCard.sectionsCard!.dueDate!}
                    auditor={auditorCard.auditor!.text}
                    actions={{
                      edit: () => handleEdit(auditorCard.auditor!.id),
                      delete: () => {
                        setShowDeleteModal(true);
                        setAuditorIdToDelete(auditorCard.auditor!.id);
                      },
                    }}
                  />
                );
              })}
            <ButtonContainer>
              <ButtonBlockColumn>
                {!myAuditCreation.sections.length && (
                  <ButtonLarge
                    title={t(
                      'COMMON.MY_ACTIONS.BOTTOM_SHEET.BUTTONS.CREATE_MY_AUDIT'
                    )}
                    onClick={() => {
                      navigate('/myaudit/checklist');
                    }}
                    outlined={true}
                    inverted={true}
                  />
                )}
                <ButtonLarge
                  title={t('COMMON.MY_AUDIT.BUTTON.SEND')}
                  backgroundColor={'oxfordBlue'}
                  height="h6"
                  onClick={handleSubmit}
                  disabled={!myAuditCreation.sections.length || isSending}
                />
              </ButtonBlockColumn>
            </ButtonContainer>
          </>
        )}
      </StyledMyAuditContainer>
      {showDeleteModal && (
        <Modal isOpen={showDeleteModal} handleModalClose={toggleDeleteModal}>
          <ButtonBlockColumn>
            <DeleteTitleContainer>
              <Headline3>
                {t('COMMON.MY_AUDIT.DETAILS.DELETE_CONFIRMATION')}
              </Headline3>
            </DeleteTitleContainer>
            <ButtonLarge
              title={t('COMMON.BOTTOM_SHEET.DELETE')}
              onClick={() => handleDelete(auditorIdToDelete)}
              backgroundColor="rustyRed"
            />
            <ButtonLarge
              title={t('COMMON.BOTTOM_SHEET.CANCEL')}
              onClick={() => setShowDeleteModal(false)}
              outlined={true}
              inverted={true}
            />
          </ButtonBlockColumn>
        </Modal>
      )}
    </>
  );
}
